import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Grid, Snackbar, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import qs from 'query-string';
import classNames from 'classnames';
import { formatDateFromIsoString } from 'utils/date-time';
import { useMeContext } from 'modules/me';
import {
  Collector,
  CollectorScope,
  CollectorSetting,
  CollectorSettingScope,
  CollectorType,
  CompanyCollectorsQuery,
  UserCollectorsQuery
} from 'graphql/types';
import { useEnabledGroupTypes } from 'modules/Settings/hooks/useEnabledGroupTypes';
import { useMessage } from 'modules/intl';
import { useSnackbarStyles } from 'modules/common/hooks/useSnackbarStyles';
import { useSidePanelContext } from 'modules/rootPage';
import { collectorInstall, isWithinAnotherApp } from '../../containerApp/index';
import CollectorsInfo from './CollectorsInfo';
import { useCollectorSettings } from './hooks';
import { useCollectorBroadcast } from './Install/hooks';
import { UserSettingDialog, CompanyCollectorSettingsDialog } from './Setting';
import { CollectorListPageSkeleton } from './CollectorListPageSkeleton';
import CollectorSyncStatusContextProvider from './contexts/CollectorSyncStatusContextProvider';

const useStyles = makeStyles((theme) => ({
  grid: {
    paddingLeft: theme.spacing(3)
  }
}));

type CollectorListPageProps = {
  scope: CollectorScope;
  collectors: {
    data: UserCollectorsQuery | CompanyCollectorsQuery | undefined;
    loading: boolean;
  };
};

export type CollectorInstallInfo = {
  syncStatus: string | undefined;
  isInstalled: boolean;
  date: string;
  installId: string;
  collectorItem: Collector;
};

export type CollectorData = {
  info: Collector;
  installInfo?: CollectorInstallInfo;
};

export const CollectorsListPage: React.FC<CollectorListPageProps> = ({
  scope,
  collectors
}: CollectorListPageProps) => {
  const me = useMeContext();
  const { data: enabledGroupTypesData, loading } = useEnabledGroupTypes();
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const disconnectSuccessMessage = useMessage(
    'collectorSetting.disconnectSuccessMessage'
  );

  const { pinned = false } = useSidePanelContext();
  const snackbarClasses = useSnackbarStyles({ pinned });

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);
  const enabledGroupTypes = useMemo(
    () =>
      !loading &&
      enabledGroupTypesData &&
      enabledGroupTypesData.enabledGroupTypes?.items
        ? enabledGroupTypesData.enabledGroupTypes?.items
        : [],
    [enabledGroupTypesData, loading]
  );
  const { data, refetch } = useCollectorSettings(
    scope === CollectorScope.User
      ? CollectorSettingScope.User
      : CollectorSettingScope.Tenant
  );

  const [openSettings, setOpenSettings] = useState(false);
  const [collectorData, setCollectorData] = useState<CollectorData | null>(
    null
  );

  const handleSettingClick = useCallback((collectorData: CollectorData) => {
    setCollectorData(collectorData);
    setOpenSettings(true);
  }, []);

  const closeSetting = useCallback(() => {
    setOpenSettings(false);
    setCollectorData(null);
  }, []);
  useCollectorBroadcast({ onBroadcast: refetch });
  const getStatus = (item: CollectorSetting): string => {
    const status = item?.settings?.find((s) => s?.key === 'syncStatus')?.value;
    if (status) {
      const retStatus = status.trim();
      if (retStatus.length > 0) return retStatus;
    }
    return (item?.installStatus?.errors?.length ?? 0) > 0 ? 'Failed' : '';
  };
  const collectorInfoObject = useMemo(
    () =>
      data?.collectorSettings?.items?.reduce((acc, item) => {
        if (item?.collectorId) {
          acc[item?.collectorId] = {
            installId: item?.installId,
            isInstalled: true,
            syncStatus: getStatus(item as CollectorSetting),
            date: formatDateFromIsoString(item?.installedDate, me),
            collectorItem: {
              id: item?.collectorId,
              name: item?.collectorMetadata?.name,
              type: item?.collectorMetadata?.type,
              retryURL: item?.collectorMetadata?.retryURL
            } as Collector
          };
        }
        return acc;
      }, {} as Record<string, CollectorInstallInfo>),
    [me, data]
  );
  const collectorInstallValue = useCallback(
    (id) => {
      let isInstalled: boolean | undefined = undefined;
      let syncStatus: string | undefined = undefined;
      let installId: string | undefined = undefined;
      let date = '';
      if (collectorInfoObject && collectorInfoObject[id]) {
        isInstalled = collectorInfoObject[id].isInstalled;
        syncStatus = collectorInfoObject[id].syncStatus;
        installId = collectorInfoObject[id].installId;
        date = collectorInfoObject[id].date;
      } else if (collectorInfoObject && !collectorInfoObject[id]) {
        isInstalled = false;
      }
      return {
        isInstalled,
        syncStatus,
        installId,
        date
      };
    },
    [collectorInfoObject]
  );

  const classes = useStyles();
  const handleConnectClick = useCallback(
    (collector) => {
      const query = qs.stringify({
        id: collector.id,
        scope: scope
      });
      const { origin } = window.location;
      const url = `${origin}/collector/install?${query}`;
      if (isWithinAnotherApp()) {
        collectorInstall(url);
      } else {
        window.open(`/collector/install?${query}`);
      }
    },
    [scope]
  );
  if (!collectors?.data) return <CollectorListPageSkeleton />;

  const collectorsList = (collectors?.data?.collectors
    .items as Collector[]).map((collector, index) => {
    const { isInstalled, date, syncStatus, installId } = collectorInstallValue(
      collector.id
    );

    return (
      <Grid item key={index} data-testid="collector-list-item">
        <CollectorsInfo
          info={collector}
          syncStatus={syncStatus}
          installId={installId}
          onConnectClick={handleConnectClick}
          onSettingClick={(info) => {
            let collectorInstallInfo =
              collectorInfoObject && collectorInfoObject[collector.id];
            if (
              info.type === CollectorType.Desktop ||
              info.type === CollectorType.Plugin
            ) {
              collectorInstallInfo = {
                isInstalled: isInstalled,
                collectorItem: {
                  ...info
                }
              } as CollectorInstallInfo;
            }
            return handleSettingClick({
              info,
              installInfo: collectorInstallInfo
            });
          }}
          isInstalled={isInstalled}
          date={date}
        />
      </Grid>
    );
  });

  return (
    <CollectorSyncStatusContextProvider>
      {collectorData?.installInfo && scope === CollectorScope.Tenant && (
        <CompanyCollectorSettingsDialog
          open={openSettings}
          onClose={closeSetting}
          collectorInfo={collectorData.info}
          collectorInstallInfo={collectorData.installInfo}
          enabledGroupTypes={enabledGroupTypes}
        />
      )}
      {collectorData?.installInfo && scope === CollectorScope.User && (
        <UserSettingDialog
          scope={scope}
          onSnackBarOpen={onSnackbarOpen}
          open={openSettings}
          onClose={closeSetting}
          collectorInstallInfo={collectorData.installInfo}
          refetch={refetch}
        />
      )}
      <Snackbar
        open={isSnackBarOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        className={classNames(snackbarClasses.snackBar)}
        onClose={onSnackbarClose}
      >
        <Alert
          data-testid="save-alert"
          severity="success"
          variant="filled"
          className={classNames(snackbarClasses.severity)}
        >
          <Typography variant="body1">{disconnectSuccessMessage}</Typography>
        </Alert>
      </Snackbar>
      <Grid
        container
        className={classes.grid}
        spacing={1}
        data-testid={`${scope}-collector-list-page`}
        alignItems="stretch"
      >
        {collectorsList}
      </Grid>
    </CollectorSyncStatusContextProvider>
  );
};
export default React.memo(CollectorsListPage);
